import { motion } from "framer-motion";
import { Check, Plus, ShoppingBag, Leaf, RotateCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { OFFERS, OFFER_END_LABEL, eur } from "./data";
import { OfferCountdown } from "./OfferCountdown";
import { useCart } from "./cart";

const ECO = [
  ["Energia rinnovabile", "Nell'Unione Europea la biomassa legnosa, come il pellet, è riconosciuta come fonte di energia rinnovabile (Direttiva UE 2018/2001)."],
  ["Ciclo naturale della CO₂", "La CO₂ liberata dalla combustione corrisponde a quella assorbita dall'albero durante la crescita."],
  ["Materia prima naturale", "Vero legno di abete, una risorsa naturale e rinnovabile."],
  ["Uso corretto", "Utilizzare in stufe e caldaie a pellet idonee, seguendo le istruzioni del produttore dell'apparecchio."],
];

const EcoBack = ({ o, onBack }) => (
  <div data-testid={`offer-eco-${o.id}`} className="face [grid-area:1/1] [transform:rotateY(180deg)] flex flex-col rounded-3xl bg-[#FDFBF7] text-[#2A1C15] p-6 sm:p-7">
    <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-[#3F6B3A]">
      <Leaf className="w-4 h-4" /> Informativa ambientale UE
    </p>
    <p className="mt-2 font-serif text-xl leading-tight">{o.name}</p>
    <ul className="mt-4 space-y-3 flex-1">
      {ECO.map(([t, d]) => (
        <li key={t} className="text-[13px] leading-snug">
          <span className="font-semibold">{t}.</span> <span className="text-[#2A1C15]/80">{d}</span>
        </li>
      ))}
    </ul>
    <p className="mt-4 text-xs text-[#2A1C15]/70">Informazioni generali sul pellet di legno nell'UE.</p>
    <button onClick={onBack} data-testid={`offer-flip-back-${o.id}`} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-[#2A1C15] px-5 py-3 text-sm font-semibold hover:bg-[#2A1C15] hover:text-[#FDFBF7] transition-colors">
      <RotateCcw className="w-4 h-4" /> Torna al prodotto
    </button>
  </div>
);

const Offer = ({ o, i }) => {
  const { add, setOpen } = useCart();
  const [done, setDone] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const featured = i === 1;

  const onAdd = () => {
    add(o.id);
    setDone(true);
    setTimeout(() => setDone(false), 1400);
    toast.success(`${o.name} aggiunto al carrello`, {
      action: { label: "Vedi carrello", onClick: () => setOpen(true) },
    });
  };

  return (
    <div data-testid={`offer-card-${o.id}`} className="relative h-full [perspective:1600px] transition-transform duration-500 hover:-translate-y-1.5">
    <motion.div
      animate={{ rotateY: flipped ? 180 : 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      style={{ transformStyle: "preserve-3d" }}
      className="grid h-full"
    >
    <article
      aria-hidden={flipped}
      className={`face [grid-area:1/1] relative flex h-full flex-col justify-between rounded-3xl p-7 ${
        featured ? "bg-[#E8893F] text-[#1A110C]" : "bg-[#241812] text-[#FDFBF7] border border-[#FDFBF7]/10"
      }`}
    >
      {o.oldPrice && (
        <span data-testid={`offer-badge-${o.id}`} className="absolute -top-3 right-6 rotate-3 rounded-full bg-[#FDFBF7] text-[#1A110C] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] shadow-lg animate-[offerPulse_2.6s_ease-in-out_infinite]">
          Offerta inverno · -{eur(o.oldPrice - o.price)}
        </span>
      )}
      <div>
        <div className="flex items-center justify-between">
          <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${featured ? "" : "text-[#E8893F]"}`}>{o.tag}</span>
          <span className="font-serif italic text-xl opacity-80">0{i + 1}</span>
        </div>
        <h3 className="mt-8 font-serif text-3xl leading-tight">{o.name}</h3>
        <p className={`mt-2 text-sm ${featured ? "text-[#1A110C]/80" : "text-[#F4EFE6]/80"}`}>{o.spec}</p>
        <p className={`mt-1 text-sm ${featured ? "text-[#1A110C]/80" : "text-[#F4EFE6]/80"}`}>Vero legno di abete</p>
      </div>
      <div className="mt-10">
        {o.oldPrice && (
          <p data-testid={`offer-oldprice-${o.id}`} className="text-lg text-[#F4EFE6]/75 line-through decoration-[#E8893F] decoration-2">{eur(o.oldPrice)} <span className="no-underline inline-block text-xs uppercase tracking-[0.15em] text-[#E8893F] ml-2" style={{ textDecoration: "none" }}>fino al {OFFER_END_LABEL}</span></p>
        )}
        <p data-testid={`offer-price-${o.id}`} className="font-serif text-5xl tracking-tight">{eur(o.price)}</p>
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={onAdd}
          data-testid={`add-to-cart-${o.id}`}
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-3.5 text-sm font-semibold transition-colors ${
            featured ? "bg-[#1A110C] text-[#FDFBF7] hover:bg-[#2A1C15]" : "bg-[#E8893F] text-[#1A110C] hover:bg-[#F4A261]"
          }`}
        >
          {done ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {done ? "Aggiunto" : "Aggiungi al carrello"}
        </motion.button>
        <button
          onClick={() => setFlipped(true)}
          data-testid={`offer-flip-${o.id}`}
          className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition-colors ${
            featured ? "border-[#1A110C]/40 hover:bg-[#1A110C]/10" : "border-[#FDFBF7]/25 hover:border-[#9BC48F] hover:text-[#9BC48F]"
          }`}
        >
          <Leaf className="w-4 h-4" /> Informativa ambientale
        </button>
      </div>
    </article>
    <EcoBack o={o} onBack={() => setFlipped(false)} />
    </motion.div>
    </div>
  );
};

export const Shop = () => {
  const { count, setOpen } = useCart();
  return (
    <section id="listino" data-testid="shop-section" className="bg-[#1A110C] text-[#FDFBF7] py-24 sm:py-36 grain relative">
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 sm:mb-20">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#E8893F] mb-5">Listino & acquisto</p>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Scegli la tua <em className="text-[#E8893F]">scorta.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-base sm:text-lg text-[#F4EFE6]/85 leading-relaxed">
              Aggiungi al carrello e invia l'ordine su WhatsApp o via email. Prezzi IVA e trasporto esclusi, consegna in 24/48h.
            </p>
            <button
              onClick={() => setOpen(true)}
              data-testid="shop-open-cart-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#FDFBF7]/40 px-5 py-3 text-sm font-semibold hover:border-[#E8893F] hover:text-[#E8893F] transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> Vai al carrello ({count})
            </button>
          </Reveal>
        </div>
        <Reveal><OfferCountdown /></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-5">
          {OFFERS.map((o, i) => (
            <Reveal key={o.id} delay={i * 0.07} className={`h-full ${i < 2 ? "lg:col-span-3" : i === OFFERS.length - 1 ? "sm:col-span-2 lg:col-span-2" : "lg:col-span-2"}`}>
              <Offer o={o} i={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
